import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type AvatarInfo, getLocalizedAvatarInfo } from '../../config/avatarMapping';
import { CoachMark } from '../CoachMark';
import './CompanionCard.css';

interface Props {
  avatar: AvatarInfo;
  onClick: (id: string) => void;
  sizeClass?: string; // 'small' | 'smaller' | 'mini' | ''
  isActive?: boolean; // Whether this is the featured/active card
  priority?: 'high' | 'low' | 'auto'; // Image loading priority
}

const CompanionCard: React.FC<Props> = ({
  avatar,
  onClick,
  sizeClass = '',
  isActive = false,
  priority = 'auto',
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [highResLoaded, setHighResLoaded] = useState(false);

  const localized = getLocalizedAvatarInfo(avatar, i18n.language);
  const displayName = localized.displayName;
  const displayTags = localized.displayTags;

  // Progressive loading: load thumb first, then upgrade to medium/large based on card size
  const thumbSrc = avatar.images?.thumb || avatar.image;
  // Active card uses large (900px), side cards use medium (600px)
  const highResSrc = isActive
    ? avatar.images?.large || avatar.images?.medium || avatar.image
    : avatar.images?.medium || avatar.image;
  const hasProgressiveImages = avatar.images && highResSrc !== thumbSrc;

  // Don't add 'active' class when using grid-card (they conflict)
  const shouldAddActive = isActive && sizeClass !== 'grid-card';
  const cardClasses =
    `companion-card ${sizeClass} ${shouldAddActive ? 'active' : ''} ${thumbLoaded ? 'image-loaded' : ''}`.trim();

  const handleThumbLoad = useCallback(() => {
    setThumbLoaded(true);
  }, []);

  const handleHighResLoad = useCallback(() => {
    setHighResLoaded(true);
  }, []);

  const handleChatClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/chat/${avatar.id}`);
    },
    [navigate, avatar.id],
  );

  return (
    // biome-ignore lint/a11y/useSemanticElements: using div instead of button to avoid nested buttons
    <div
      className={cardClasses}
      role="button"
      tabIndex={0}
      onClick={() => onClick(avatar.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick(avatar.id);
        }
      }}
      aria-label={
        t
          ? t('home.companion.viewInfo', 'View info for {{name}}', { name: displayName })
          : `View info for ${displayName}`
      }
    >
      <div className="card-image-outer">
        {!thumbLoaded && <div className="card-image-placeholder" />}
        {hasProgressiveImages ? (
          <>
            {/* Thumb image - loads first for instant display (7KB) */}
            <img
              src={thumbSrc}
              alt={displayName}
              className={`card-image card-image-thumb ${thumbLoaded ? 'loaded' : ''}`}
              loading="eager"
              decoding="async"
              // @ts-expect-error fetchpriority is valid but not in React types yet
              fetchpriority="high"
              onLoad={handleThumbLoad}
            />
            {/* High-res image - loads after thumb, fades in on top (23KB) */}
            <img
              src={highResSrc}
              alt={displayName}
              className={`card-image card-image-highres ${highResLoaded ? 'loaded' : ''}`}
              loading="lazy"
              decoding="async"
              // @ts-expect-error fetchpriority is valid but not in React types yet
              fetchpriority="low"
              onLoad={handleHighResLoad}
            />
          </>
        ) : (
          <img
            src={avatar.image}
            alt={displayName}
            className={`card-image ${thumbLoaded ? 'loaded' : ''}`}
            loading="eager"
            decoding="async"
            // @ts-expect-error fetchpriority is valid but not in React types yet
            fetchpriority={isActive || priority === 'high' ? 'high' : 'low'}
            onLoad={handleThumbLoad}
          />
        )}
      </div>

      <div className="card-overlay" />

      {/* Simple name for non-active cards (bottom) */}
      <div className="card-simple-name">
        <span className="card-title-en">{displayName}</span>
      </div>

      {/* Full Content Frame - Only visible on spotlight card */}
      <div className="card-content-frame">
        <div className="card-content-inner">
          <div className="card-info-top">
            <div className="card-title-row">
              <h3 className="card-title-en">{displayName}</h3>
            </div>

            <div className="card-date-row">
              <span className="card-date-text">{localized.lifespan || '601-661 CE'}</span>
            </div>
          </div>

          <div className="card-tags-divider" />

          <div className="card-tags-figma">
            {displayTags ? (
              displayTags.slice(0, 2).map((tag) => (
                <div key={tag} className="tag-badge-figma">
                  <span>{tag}</span>
                </div>
              ))
            ) : (
              <>
                <div className="tag-badge-figma">
                  <span>Fourth Caliph</span>
                </div>
                <div className="tag-badge-figma">
                  <span>Prophet's Cousin</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Floating Chat Button Inside Frame */}
        <CoachMark
          id="companion-chat"
          message={t('home.companion.chatCoachMark', 'Start a conversation with this companion.')}
          buttonText={t('chat.gotIt', 'Got it')}
          position="right"
          wrapperClassName="coach-mark-chat-wrapper"
        >
          <button
            type="button"
            className="chat-btn-figma"
            onClick={handleChatClick}
            data-tooltip={t('home.companion.startChat', 'Start conversation')}
            aria-label={t('home.companion.startChat', 'Start conversation')}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeWidth="1.8"
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z"
              />
              <path strokeWidth="2.5" d="M17 12H17.01M12 12H12.01M7 12H7.01" />
            </svg>
          </button>
        </CoachMark>
      </div>
    </div>
  );
};

export default CompanionCard;
